"use client";

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type React from "react";
import { useDialog, useOverlay, usePreventScroll, FocusScope } from "react-aria";
import { useOverlayTriggerState } from "react-stately";
import { mergeProps } from "@react-aria/utils";
import { cn } from "../../utils/cn";
import type {
  DialogAnimationState,
  DialogContextValue,
  DialogHeadlessProps,
  DialogVariant,
} from "./Dialog.types";

// ─── Context ──────────────────────────────────────────────────────────────────

/**
 * Context shared between `DialogHeadless` and slot sub-components
 * (`DialogHeadline`, `DialogContent`, `DialogActions`).
 *
 * Provides stable IDs for aria-labelledby / aria-describedby wiring,
 * the close callback, and the active variant.
 *
 * @internal
 */
export const DialogContext = createContext<DialogContextValue | null>(null);

/**
 * Hook to consume `DialogContext` inside dialog slot sub-components.
 * Throws a descriptive error if used outside a `DialogHeadless` tree.
 *
 * @internal
 */
export function useDialogContext(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (ctx === null) {
    throw new Error(
      "[Dialog] DialogHeadline, DialogContent, and DialogActions must be rendered " +
        "inside a <Dialog> or <DialogHeadless> component."
    );
  }
  return ctx;
}

// ─── DialogPanel (internal) ───────────────────────────────────────────────────

/**
 * Inner dialog panel — wires React Aria hooks (`useDialog`, `useOverlay`,
 * `usePreventScroll`) and renders the accessible dialog element with the
 * centering wrapper.
 *
 * DOM structure:
 * ```
 *   <div> (centering / positioning wrapper, z-50)
 *     <div role="dialog" aria-modal> (panel — className, animation, data attrs)
 *       [icon element — only when icon prop is set]
 *       {children}
 *     </div>
 *   </div>
 * ```
 *
 * This allows `screen.getByRole("dialog")` to directly return the panel
 * (with `data-animation-state` and `data-variant` for test assertions).
 *
 * @internal
 */
interface DialogPanelProps {
  ariaLabel: string | undefined;
  headlineId: string;
  contentId: string;
  onClose: () => void;
  onAnimationEnd: (e: React.AnimationEvent<HTMLDivElement>) => void;
  variant: DialogVariant;
  isDismissable: boolean;
  wrapperClassName: string;
  className: string | undefined;
  animationState: DialogAnimationState;
  getAnimationClassName: ((state: DialogAnimationState) => string) | undefined;
  icon: React.ReactNode | undefined;
  children: React.ReactNode;
}

const DialogPanel = ({
  ariaLabel,
  headlineId,
  contentId,
  onClose,
  onAnimationEnd,
  variant,
  isDismissable,
  wrapperClassName,
  className,
  animationState,
  getAnimationClassName,
  icon,
  children,
}: DialogPanelProps): React.ReactElement => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock body scroll while dialog is open
  usePreventScroll();

  // useDialog: applies role="dialog", aria-modal="true", aria-labelledby, aria-describedby
  const { dialogProps } = useDialog(
    {
      ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      "aria-labelledby": headlineId,
      "aria-describedby": contentId,
    },
    panelRef
  );

  // useOverlay: handles Escape key dismissal and outside-click dismissal
  const { overlayProps } = useOverlay(
    {
      isOpen: true,
      onClose,
      isDismissable,
      shouldCloseOnBlur: false,
    },
    panelRef
  );

  // Merge animation classes onto the panel element
  const panelClassName = cn(className, getAnimationClassName?.(animationState));

  // `data-with-icon` is a content flag (presence-based): attribute present = icon is rendered.
  // This lets `group-data-[with-icon]/dialog:` selectors in headline/content center their text.
  const hasIcon = icon !== undefined && icon !== null && variant === "basic";

  return (
    // Centering/positioning wrapper — structural only, no ARIA role
    <div className={wrapperClassName}>
      {/* Panel: semantic dialog element with React Aria hooks, animation, data attrs */}
      <div
        {...mergeProps(overlayProps, dialogProps)}
        ref={panelRef}
        aria-modal="true"
        className={panelClassName}
        data-animation-state={animationState}
        data-variant={variant}
        // Presence-based content flag — drives centering via group-data-[with-icon]/dialog:
        data-with-icon={hasIcon ? "" : undefined}
        // onAnimationEnd advances "exiting" → "exited" after the exit keyframe completes.
        // We guard e.target === e.currentTarget so that animationend events bubbling up
        // from child elements (e.g. ripple animations on action buttons) do not
        // accidentally advance the state machine.
        onAnimationEnd={onAnimationEnd}
      >
        {/* Optional hero icon — rendered above headline, centered per MD3 spec */}
        {hasIcon && icon}

        {children}
      </div>
    </div>
  );
};

DialogPanel.displayName = "DialogPanel";

// ─── DialogHeadless ───────────────────────────────────────────────────────────

/**
 * `DialogHeadless` — Layer 2 headless primitive.
 *
 * Provides all MD3 Dialog behavior and ARIA semantics without any visual styling:
 *
 * - **Portal rendering**: dialog and scrim render in `document.body` via
 *   `createPortal` to avoid stacking context issues.
 * - **Open/close state**: via `useOverlayTriggerState` (supports controlled
 *   `open` + `onOpenChange` and uncontrolled `defaultOpen`).
 * - **Scroll lock**: `usePreventScroll` locks body scroll while open.
 * - **Focus trap**: `FocusScope` with `contain`, `restoreFocus`, `autoFocus`.
 * - **Dismiss behavior**: `useOverlay` handles Escape key and outside click.
 *   Basic variant is dismissable (Escape + outside click);
 *   Full-screen variant is Escape-dismissable only (no scrim click) per MD3 spec.
 * - **Animation state machine**: `entering → visible → exiting → exited`
 *   Exit advances on `onAnimationEnd` with `e.target === e.currentTarget` guard
 *   (prevents child animation events from advancing the state machine), plus a
 *   250ms fallback timer in case the animation event doesn't fire.
 * - **Scrim animation**: state-driven via `getScrimClassName(animationState)`.
 * - **Hero icon**: optional `icon` prop renders centered above headline;
 *   sets `data-with-icon` on panel root for group-data centering of headline/content.
 * - **ARIA wiring**: `DialogContext` provides stable IDs for `aria-labelledby`
 *   and `aria-describedby`, consumed by slot sub-components.
 *
 * React Aria hooks used:
 * - `useDialog` — `role="dialog"`, `aria-modal`, `aria-labelledby`, `aria-describedby`
 * - `useOverlay` — Escape key + outside-click dismissal
 * - `usePreventScroll` — body scroll lock
 * - `FocusScope` — focus trap + restoreFocus on close
 * - `useOverlayTriggerState` — open/close state management
 *
 * @example
 * ```tsx
 * // Controlled basic dialog
 * <DialogHeadless
 *   variant="basic"
 *   open={open}
 *   onOpenChange={setOpen}
 *   className={cn(dialogPanelVariants({ variant: 'basic' }))}
 *   wrapperClassName={dialogWrapperVariants({ variant: 'basic' })}
 *   getScrimClassName={(state) => dialogScrimVariants({ animationState: state })}
 *   getAnimationClassName={(state) =>
 *     dialogAnimationVariants({ animationState: state, variant: 'basic' })
 *   }
 * >
 *   <DialogHeadline>Confirm?</DialogHeadline>
 *   <DialogContent>This action cannot be undone.</DialogContent>
 *   <DialogActions>
 *     <Button variant="text" onPress={() => setOpen(false)}>Cancel</Button>
 *     <Button variant="filled" onPress={handleDelete}>Delete</Button>
 *   </DialogActions>
 * </DialogHeadless>
 * ```
 */
export const DialogHeadless = forwardRef<HTMLDivElement, DialogHeadlessProps>(
  function DialogHeadless(
    {
      variant = "basic",
      open,
      defaultOpen = false,
      onOpenChange,
      "aria-label": ariaLabel,
      icon,
      children,
      className,
      wrapperClassName,
      getScrimClassName,
      getAnimationClassName,
    },
    _ref
  ) {
    // ── Open/close state ─────────────────────────────────────────────────────

    const state = useOverlayTriggerState({
      ...(open !== undefined ? { isOpen: open } : {}),
      ...(defaultOpen !== undefined ? { defaultOpen } : {}),
      ...(onOpenChange !== undefined ? { onOpenChange } : {}),
    });

    const isOpen = state.isOpen;

    const close = useCallback(() => {
      state.close();
    }, [state]);

    // ── Animation state machine ───────────────────────────────────────────────

    const [animationState, setAnimationState] = useState<DialogAnimationState>("exited");
    // Guard against calling state updates after unmount / multiple times
    const closedRef = useRef<boolean>(false);
    // Fallback timer for exit animation in case onAnimationEnd doesn't fire
    // (e.g. browser reduced-motion CSS override, animation cancelled)
    const exitFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // When isOpen becomes true → start entry animation cycle
    useEffect(() => {
      if (!isOpen) return;

      closedRef.current = false;
      setAnimationState("entering");

      // Zero-delay timer ensures "entering" state is rendered (allowing CSS
      // animation initial keyframe to paint) before transitioning to "visible".
      const id = setTimeout(() => {
        setAnimationState("visible");
      }, 0);

      return () => clearTimeout(id);
    }, [isOpen]);

    // When isOpen becomes false while visible → start exit animation cycle
    useEffect(() => {
      if (isOpen) return;
      if (animationState === "exited" || animationState === "entering") return;

      if (animationState === "visible") {
        setAnimationState("exiting");

        // 250ms fallback — longer than the 200ms exit keyframe so it only fires
        // if onAnimationEnd doesn't (e.g. user's CSS `animation: none` override
        // at the app level rather than via prefers-reduced-motion).
        exitFallbackRef.current = setTimeout(() => {
          if (!closedRef.current) {
            closedRef.current = true;
            setAnimationState("exited");
          }
        }, 250);
      }
    }, [isOpen, animationState]);

    // Cleanup on unmount
    useEffect(
      () => () => {
        if (exitFallbackRef.current !== null) {
          clearTimeout(exitFallbackRef.current);
        }
      },
      []
    );

    // Advances exiting → exited after the CSS keyframe animation ends.
    // e.target === e.currentTarget guard: ensures only the panel's own
    // animationend event triggers the advance — not events bubbling up
    // from child elements (e.g. ripple animations inside DialogActions).
    const handleAnimationEnd = useCallback(
      (e: React.AnimationEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (animationState === "exiting" && !closedRef.current) {
          if (exitFallbackRef.current !== null) {
            clearTimeout(exitFallbackRef.current);
            exitFallbackRef.current = null;
          }
          closedRef.current = true;
          setAnimationState("exited");
        }
      },
      [animationState]
    );

    // ── ARIA ID coordination ──────────────────────────────────────────────────

    const baseId = useId();
    const headlineId = `${baseId}-dialog-headline`;
    const contentId = `${baseId}-dialog-content`;

    const contextValue: DialogContextValue = {
      headlineId,
      contentId,
      close,
      variant,
    };

    // ── Scrim dismissal ───────────────────────────────────────────────────────

    // Basic variant: scrim click closes dialog. Full-screen: scrim is inert.
    const handleScrimClick = useCallback(() => {
      if (variant === "basic") {
        close();
      }
    }, [variant, close]);

    // ── Derived wrapper class ─────────────────────────────────────────────────

    // Use the caller-supplied wrapperClassName when provided; fall back to the
    // hardcoded centering strings so DialogHeadless still works standalone.
    const resolvedWrapperClass =
      wrapperClassName ??
      (variant === "basic"
        ? "fixed inset-0 z-50 flex items-center justify-center px-4"
        : "fixed inset-0 z-50");

    // ── Derived scrim class ───────────────────────────────────────────────────

    // Use the caller-supplied getScrimClassName when provided; fall back to a
    // static scrim without animation so DialogHeadless works as a standalone primitive.
    const resolvedScrimClass =
      getScrimClassName?.(animationState) ?? "fixed inset-0 z-40 bg-scrim/32";

    // ── Portal gate ───────────────────────────────────────────────────────────

    // Do not render portal until open, and remove once animation is fully exited
    if (!isOpen && animationState === "exited") {
      return null;
    }

    // ── Portal content ────────────────────────────────────────────────────────

    const content = (
      <DialogContext.Provider value={contextValue}>
        {/* Scrim overlay — click closes for basic, inert for fullscreen */}
        <div
          data-testid="dialog-scrim"
          className={resolvedScrimClass}
          onClick={handleScrimClick}
          aria-hidden="true"
        />

        {/* FocusScope: traps focus, restores focus to trigger on close, auto-focuses first element */}
        <FocusScope contain restoreFocus autoFocus>
          <DialogPanel
            ariaLabel={ariaLabel}
            headlineId={headlineId}
            contentId={contentId}
            onClose={close}
            onAnimationEnd={handleAnimationEnd}
            variant={variant}
            isDismissable={variant === "basic"}
            wrapperClassName={resolvedWrapperClass}
            className={className}
            animationState={animationState}
            getAnimationClassName={getAnimationClassName}
            icon={icon}
          >
            {children}
          </DialogPanel>
        </FocusScope>
      </DialogContext.Provider>
    );

    if (typeof document === "undefined") return null;

    return createPortal(content, document.body) as React.ReactElement;
  }
);

DialogHeadless.displayName = "DialogHeadless";
